import { useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { AppShell } from '@/shared/ui/AppShell'
import { Skeleton } from '@/shared/ui/Skeleton'
import { ErrorState } from '@/shared/ui/States'
import { cn } from '@/lib/utils'
import type { GraphNode } from '@/features/metrics/model/contracts'
import { getTopicDetail } from '@/features/topics/api/topicsClient'
import { useTopicNetwork } from '../state/useTopicNetwork'
import { useNetworkKeywordFilter } from '../state/useNetworkKeywordFilter'
import { useNetworkLayout } from '../state/useNetworkLayout'
import { NetworkGraph } from './NetworkGraph'
import { NetworkLayoutPicker } from './NetworkLayoutPicker'
import { NetworkHeaderKpis } from './NetworkHeaderKpis'
import { NetworkAnalyticsPanel } from './NetworkAnalyticsPanel'
import { NetworkKeywordChips } from './NetworkKeywordChips'
import { NodeDetailDrawer } from './NodeDetailDrawer'
import type { NetworkRange } from '../model/contracts'

const RANGES: NetworkRange[] = ['7d', '30d', '90d', 'all']

export function NetworkPage() {
  const { topicId = '' } = useParams<{ topicId: string }>()
  const { t } = useTranslation('network')
  const { data, isLoading, error, range, setRange } = useTopicNetwork(topicId)
  const { keywordFilter, setKeywordFilter } = useNetworkKeywordFilter()
  const { layout, setLayout } = useNetworkLayout()
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  const handleNodeClick = useCallback((node: GraphNode) => {
    setSelectedNode((prev) => (prev?.id === node.id ? null : node))
  }, [])
  const handleDrawerClose = useCallback(() => setSelectedNode(null), [])

  const { data: topicDetail } = useQuery({
    queryKey: ['topics', topicId, 'detail'],
    queryFn: ({ signal }) => getTopicDetail(topicId, signal),
    staleTime: 300_000,
    enabled: Boolean(topicId),
  })

  const topicKeywords = topicDetail?.keywords ?? []

  return (
    <AppShell showAnalytics={false} showTopBar>
      <div className="flex h-full flex-col gap-4 px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold text-foreground">{t('title')}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <NetworkLayoutPicker layout={layout} onChange={setLayout} />
            <div className="flex items-center gap-1 rounded-full bg-secondary/30 p-0.5 ring-1 ring-primary/15">
              {RANGES.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className={cn(
                    'min-h-[36px] rounded-full px-3 py-1.5 text-xs font-semibold transition-all active:scale-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none',
                    range === r
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`range.${r}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="min-h-[60vh] flex-1">
          {isLoading && (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="min-h-[60vh] w-full rounded-xl" />
            </div>
          )}

          {error && !isLoading && <ErrorState error={error} />}

          {data && !isLoading && <NetworkHeaderKpis stats={data.stats} />}

          {data && !isLoading && topicKeywords.length > 0 && (
            <div className="mt-3">
              <NetworkKeywordChips
                topicKeywords={topicKeywords}
                value={keywordFilter}
                onChange={setKeywordFilter}
              />
            </div>
          )}

          {data && !isLoading && data.nodes.length === 0 && (
            <div className="flex min-h-[60vh] items-center justify-center rounded-xl border border-border bg-card">
              <p className="text-sm text-muted-foreground">{t('emptyState')}</p>
            </div>
          )}

          {data && !isLoading && data.nodes.length > 0 && (
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="lg:w-[70%]">
                <NetworkGraph
                  response={data}
                  highlightKeywords={keywordFilter}
                  onNodeClick={handleNodeClick}
                  layout={layout}
                />
              </div>
              <div className="lg:w-[30%]">
                <NetworkAnalyticsPanel data={data} />
              </div>
            </div>
          )}
        </div>
      </div>

      <NodeDetailDrawer
        node={selectedNode}
        topicId={topicId}
        onClose={handleDrawerClose}
      />
    </AppShell>
  )
}
